import "../../assets/css/clientAlerts/holdPaymentList.css"

import sortIcon from "../../assets/images/sort.svg";
import industry from "../../assets/images/industries.svg";
import location from "../../assets/images/location.svg";
import starRating from "../../assets/images/starRating.svg";
import close from "../../assets/images/close.svg";
import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../../context/auth-user";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { getHoldPaymentList, getHoldPaymentListFilters } from "./holdPaymentListUtils";
import PageSelector from "../pageSelector";

const ITEMS_PER_PAGE = 20;

export default function HoldPaymentList() {
    const { user } = useAuth();
    const userLogged = user?.selectedProfile;
    const darkLightMode = user?.selectedProfile?.darkLightMode;
    const navigate = useNavigate();

    // Data paginada desde el backend
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);

    const [searchValue, setSearchValue] = useState("")
    const [searchValueApplied, setSearchValueApplied] = useState("")

    // Filtros y UI
    const [industryFilter, setIndustryFilter] = useState([]);
    const [locationFilter, setLocationFilter] = useState([]);

    const [industryFilterApplied, setIndustryFilterApplied] = useState([]);
    const [locationFilterApplied, setLocationFilterApplied] = useState([]);

    const [industrySearch, setIndustrySearch] = useState("");
    const [locationSearch, setLocationSearch] = useState("");
    const [openFilter, setOpenFilter] = useState(null);
    const [resetAll, setResetAll] = useState(false);
    const filterRef = useRef(null);

    // Ordenamiento
    const [order, setOrder] = useState("ASC");
    const [orderBy, setOrderBy] = useState("companyName");

    // Datos para construir los filtros
    const [filtersData, setFiltersData] = useState({ 
        industries: [], 
        locations: [] 
    });

     // Close menus filters on click outside
    useEffect(() => {
        const closeMenusFilters = (e) => {
            if (!filterRef?.current?.contains(e.target)) {
                setOpenFilter(null);
            }
        };

        document.body.addEventListener("mousedown", closeMenusFilters);

        return () => document.body.removeEventListener("mousedown", closeMenusFilters);
    }, []);

    // Efecto: Obtener opciones de filtros del backend (una sola vez)
    useEffect(() => {
        if (!userLogged) return;

        const controller = new AbortController();

        const { signal } = controller
        
        const params = {
            rol: userLogged?.rol,
            dataID: userLogged?.dataID
        }

        getHoldPaymentListFilters({ signal, params }).then(data => {
            setFiltersData(data)
        }).catch(err => console.error(err))
        
        return () => controller.abort();
    }, [userLogged]);

    // Efecto: Cada cambio de filtro, paginación, orden manda a traer data filtrada/paginada del back
    useEffect(() => {
        if (!userLogged) return;

        const controller = new AbortController();

        const { signal } = controller

        const params = {
            rol: userLogged?.rol,
            dataID: userLogged?.dataID,
            searchValue: searchValueApplied,
            industry: industryFilterApplied,
            location: locationFilterApplied,
            order,
            orderBy,
            page: currentPage,
            limit: ITEMS_PER_PAGE
        }

        getHoldPaymentList({ params, signal }).then(data => {
            setData(data?.holdPaymentList ?? [])
            setTotal(data?.total ?? 0)
        }).catch(err => console.error(err));

        return () => controller.abort();
    }, [userLogged, searchValueApplied, industryFilterApplied, locationFilterApplied, order, orderBy, currentPage]);

    // Opciones para filtros UI (buscador)
    const filteredIndustries = (filtersData?.industries ?? []).filter(ind => !industrySearch || ind.toLowerCase().includes(industrySearch.toLowerCase()));
    const filteredLocations = (filtersData?.locations ?? []).filter(loc => !locationSearch || loc.toLowerCase().includes(locationSearch.toLowerCase()));

    // Handlers UI
    const handleOpenFilter = useCallback((e, filterName) => {
        filterRef.current = e.target;
        setOpenFilter(prev => prev === filterName ? null : filterName);
    }, []);

    const toggleFilterValue = useCallback((setFilter) => (target) => {
        setFilter(prev => prev.includes(target.value)
            ? prev.filter(val => val !== target.value)
            : [...prev, target.value]);
    }, []);

    // Reset all
    useEffect(() => {
        setResetAll(industryFilterApplied.length > 0 || locationFilterApplied.length > 0);
    }, [industryFilterApplied, locationFilterApplied]);

    const applyFilter = useCallback((filter) => {
        setCurrentPage(1); // Opcional: regresa a la página 1 al filtrar
        if (searchValueApplied || searchValue) {
            setSearchValue("")
            setSearchValueApplied("");
        }
        
        if (filter === "industry") {
            return setIndustryFilterApplied(industryFilter);
        } 
        
        if (filter === "location") {
            return setLocationFilterApplied(locationFilter);
        }
    }, [searchValue, searchValueApplied, industryFilter, locationFilter])

    const clearFilter = useCallback((filter) => {
        switch (filter) {
            case "industry":
                setIndustryFilter([]);
                setIndustryFilterApplied([]);
                break;
            case "location":
                setLocationFilter([]);
                setLocationFilterApplied([]);
                break;
            default:
                setIndustryFilter([]);
                setLocationFilter([]);
                setIndustryFilterApplied([]);
                setLocationFilterApplied([]);
                break;
        }

        setCurrentPage(1);
    }, []);

    // Sort
    const sortTable = useCallback((sortBy) => {
        setOrder(prev => prev === "ASC" ? "DSC" : "ASC");
        setOrderBy(sortBy);
    }, []);

    // Paginación
    const totalPages = Math.ceil(total / ITEMS_PER_PAGE);
    const paginate = (pageNumber) => {
        if (pageNumber < 1 || pageNumber > totalPages) return;
        setCurrentPage(pageNumber);
    };

    const handleSearchValue = useCallback((e) => {
        const { value } = e.target

        setSearchValue(value);
    }, []);

    const handleApplySearchValue = useCallback((e) => {
        e.preventDefault();

        if (industryFilter?.length > 0 || industryFilterApplied?.length > 0) {
            setIndustryFilter([])
            setIndustryFilterApplied([])
        }

        if (locationFilter?.length > 0 || locationFilterApplied?.length > 0) {
            setLocationFilter([])
            setLocationFilterApplied([])
        }

        setSearchValueApplied(searchValue)
    }, [searchValue, industryFilter, industryFilterApplied, locationFilter, locationFilterApplied])

    // Estilos
    const mainStyle = {
        background: darkLightMode ? "#131E29" : "white",
        color: darkLightMode ? "white" : "inherit"
    };

    return (
        <section className={`holdPayment ${darkLightMode ? "darkMode" : null}`}>
            <div className="filters">
                {/* Filtro Industry */}
                <div className={`holdPaymentFilter ${openFilter === "industry" ? "show" : null} ${industryFilterApplied.length ? "active" : undefined}`} onClick={e => handleOpenFilter(e, "industry")}>
                    <img src={industry} alt="Prodensa Supplier" />
                    <span>Industry</span>
                    <div className={`searchContainer ${openFilter === "industry" ? "show" : null}`} onClick={e => e.stopPropagation()}>
                        <input value={industrySearch} type="text" placeholder="Search industries" onChange={e => setIndustrySearch(e.target.value)} />
                    </div>
                    <div className={`holdPaymentFilterOptions location ${openFilter === "industry" ? "show" : null}`} onClick={e => !e.target.matches("button") && e.stopPropagation()}>
                        <img src={close} alt="Prodensa Supplier" onClick={() => setOpenFilter(null)} className="closeImg d-lg-none" />
                        <span className="title"><img className="d-lg-none imgMenu" src={industry} alt="Prodensa Supplier" />Industry</span>
                        <div className="optionsScroll">
                            {filteredIndustries.map((ind, idx) => (
                                <div key={idx} className="option">
                                    <input checked={industryFilter.includes(ind)} className="industryCheck" value={ind} type="checkbox" id={`industry${idx}`} onChange={e => toggleFilterValue(setIndustryFilter)(e.target)} />
                                    <label htmlFor={`industry${idx}`}>{ind}</label>
                                </div>
                            ))}
                        </div>
                        <div className="actions">
                            <button onClick={() => clearFilter("industry")}>Clear</button>
                            <button onClick={() => applyFilter("industry")}>Apply</button>
                        </div>
                    </div>
                </div>
                {/* Filtro Location */}
                <div className={`holdPaymentFilter location ${openFilter === "location" ? "show" : null} ${locationFilterApplied.length ? "active" : undefined}`} onClick={e => handleOpenFilter(e, "location")}>
                    <img src={location} alt="Prodensa Supplier" />
                    <span>Location</span>
                    <div className={`searchContainer ${openFilter === "location" ? "show" : null}`} onClick={e => e.stopPropagation()}>
                        <input value={locationSearch} type="text" placeholder="Search cities" onChange={e => setLocationSearch(e.target.value)} />
                    </div>
                    <div className={`holdPaymentFilterOptions location ${openFilter === "location" ? "show" : null}`} onClick={e => !e.target.matches("button") && e.stopPropagation()}>
                        <img src={close} alt="Prodensa Supplier" onClick={() => setOpenFilter(null)} className="closeImg d-lg-none" />
                        <span className="title"><img className="d-lg-none imgMenu" src={location} alt="Prodensa Supplier" />Location</span>
                        <div className="optionsScroll">
                            {filteredLocations.map((loc, idx) => (
                                <div key={idx} className="option">
                                    <input checked={locationFilter.includes(loc)} className="locationCheck" value={loc} type="checkbox" id={`location${idx}`} onChange={e => toggleFilterValue(setLocationFilter)(e.target)} />
                                    <label htmlFor={`location${idx}`}>{loc}</label>
                                </div>
                            ))}
                        </div>
                        <div className="actions">
                            <button onClick={() => clearFilter("location")}>Clear</button>
                            <button onClick={() => applyFilter("location")}>Apply</button>
                        </div>
                    </div>
                </div>

                {/* Reset all */}
                <span className={`resetAll ${resetAll ? "show" : ""}`} onClick={() => clearFilter()}>Reset All</span>

                <form onSubmit={handleApplySearchValue}>
                    <div className={`searchContainer ${darkLightMode ? "darkMode" : null}`}>
                        <input id="generalSearch" value={searchValue} type="text" placeholder="Search by name..."
                            onChange={handleSearchValue} />

                        <button className="applySearchButton" type="submit"></button>
                    </div>
                </form>
            </div>
            {/* Tabla */}
            <table className="tableHoldPayment">
                <thead>
                    <tr>
                        <th style={mainStyle}>
                            <span>Name</span>
                            <img className="sort" src={sortIcon} alt="Prodensa supplier" onClick={() => sortTable("companyName")} />
                        </th>
                        <th style={mainStyle}>
                            <span>Industry</span>
                            <img className="sort" src={sortIcon} alt="Prodensa supplier" onClick={() => sortTable("industries")} />
                        </th>
                        <th style={mainStyle}>
                            <span>Location</span>
                            <img className="sort" src={sortIcon} alt="Prodensa supplier" onClick={() => sortTable("location")} />
                        </th>
                        <th style={mainStyle}>
                            <span>Rating</span>
                            <img className="sort" src={sortIcon} alt="Prodensa supplier" onClick={() => sortTable("rating")} />
                        </th>
                        <th style={mainStyle}>
                            <span>Date</span>
                            <img className="sort" src={sortIcon} alt="Prodensa supplier" onClick={() => sortTable("holdPaymentDate")} />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((supplier, idx) => (
                        <tr key={idx}>
                            <td>
                                <div className="infoContainer">
                                    <span onClick={() => navigate("/home/suppliers/supplierProfile/" + supplier._id)}>
                                        {supplier.companyName}
                                    </span>
                                </div>
                            </td>
                            <td>
                                <div className="infoContainer">
                                    <span>{(supplier.industries || []).join(", ")}</span>
                                </div>
                            </td>
                            <td>
                                <div className="infoContainer">
                                    <span>{supplier.location}</span>
                                </div>
                            </td>
                            <td>
                                <div className="infoContainer">
                                    <span>{supplier.rating?.$numberDecimal ?? "Unrated"}</span>
                                    {supplier.rating?.$numberDecimal && <img className="starRating" src={starRating} alt="Prodensa Supplier" />}
                                </div>
                            </td>
                            <td>
                                <div className="infoContainer">
                                    <span>
                                        {moment(supplier.holdPaymentDate).format("DD MMMM YYYY")}
                                    </span>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className="d-lg-none holdPaymentList">
                {data?.map((supplier, index) => (
                    <div key={index} className="holdPaymentRow" onClick={() => navigate("/home/suppliers/supplierProfile/" + supplier._id)}>
                        <h5 className="holdPaymentName">{supplier.companyName}</h5>
                        <div className="holdPaymentInformation">
                            <span className="holdPaymentValue">{supplier.industries.map((industry, index) => {
                                return supplier.industries.length > index + 1 ? industry + ", " : industry
                            })}</span>
                            <span className="holdPaymentTitle">Industry</span>
                            <span className="holdPaymentValue">
                                {supplier.location}
                            </span>
                            <span className="holdPaymentTitle">Location</span>
                            <span className="holdPaymentValue">
                                {supplier.rating?.$numberDecimal ?? "Unrated"}
                                {supplier.rating?.$numberDecimal && <img className="starRating" src={starRating} alt="Prodensa Supplier" />}
                            </span>
                            <span className="holdPaymentTitle">Rating</span>
                            <span className="holdPaymentValue">{moment(supplier.holdPaymentDate).format("DD MMMM YYYY")}</span>
                            <span className="holdPaymentTitle">Date</span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Paginación */}
            <div className="addAndPagination">
                <PageSelector
                    currentPage={currentPage}
                    totalItems={total || 0}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onChange={paginate}
                />
            </div>

            <span className="tableCount">{total < ITEMS_PER_PAGE * currentPage ? total : ITEMS_PER_PAGE * currentPage} of {total}</span>
        </section>
    );
}
